import _ from 'lodash'
import {mapActions, mapState} from 'vuex'
import auth from "./auth"
import axios from 'axios'

let paginator_mixin = {
    data() {
        return {
            pagination: {
                current_page: 1,
                per_page: 10
            },
            // this should be overridden by the component extending this mixin
            dataKey: ''
        }
    },
    computed: {
        pageCount() {
            return Math.ceil(_.get(this, this.dataKey).length / this.pagination.per_page)
        },

        paginatedData() {
            let page_number = this.pagination.current_page - 1
            return _.get(this, this.dataKey).slice(page_number * this.pagination.per_page, (page_number + 1) * this.pagination.per_page)
        },

        dataCount() {
            return _.get(this, this.dataKey).length
        }
    },
    methods: {
        changePage(page) {
            this.pagination.current_page = page
        },

        handleSizeChange(val) {
            this.pagination.per_page = val
        }
    }
}

let validator_mixin = {
    methods: {
        domainValidator(rule, value, callback) {
            if (!value || typeof value !== 'string') {
                return callback(new Error("Invalid domain"))
            }
            let labels = value.split('.')
            for (let label_index in labels) {
                if (labels[label_index].length > 63) {
                    return callback(new Error("Domain labels should only have a maximum of 63 characters"))
                }
            }
            if (value.length === 0) {
                return callback()
            }
            let domain_regex = /^(?:(?:(?:[a-zA-z\-]+)\:\/{1,3})?(?:[a-zA-Z0-9])(?:[a-zA-Z0-9-\.]){1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+|\[(?:(?:(?:[a-fA-F0-9]){1,4})(?::(?:[a-fA-F0-9]){1,4}){7}|::1|::)\]|(?:(?:[0-9]{1,3})(?:\.[0-9]{1,3}){3}))(?:\:[0-9]{1,5})?$/
            if (!domain_regex.test(value)) {
                return callback(new Error("Invalid domain"))
            }
            callback()
        }
    }
}

let form_validation_mixin = {
    data() {
        return {
            validated: false
        }
    },

    methods: {
        preValidateFormLogic: function (form_name, reset = false, force_validate = false, override = null) {
            let form_element = _.get(this.$refs, form_name, null)

            if (!form_element) {
                return
            }

            if (reset == true) {
                form_element.clearValidate()

                // halt if we don't want to continue the validation
                if (!force_validate) {
                    return
                }
            }

            let fields = form_element.fields
            if (fields.find((f) => f.validateState === 'validating')) {
                setTimeout(() => {
                    this.preValidateFormLogic(form_name)
                }, 100)

                return
            }

            this.validated = this.validateForm(form_name)

            if (override === false && this.validated) {
                this.validated = false
            }
        },

        preValidateForm: _.debounce(function (form_name, reset = false, force_validate = false, override = null) {
            this.preValidateFormLogic(form_name, reset, force_validate, override)
        }, 100),

        preValidateFormNow: function (form_name, reset = false, force_validate = false, override = null) {
            this.preValidateFormLogic(form_name, reset, force_validate, override)
        },

        validateForm(form_name) {
            let form_element = _.get(this.$refs, form_name, null)

            if (!form_element) {
                return false
            }

            let res = null
            form_element.validate((valid) => {
                res = valid ? true : false
            })

            if (res) {
                form_element.clearValidate()
            }

            return res
        },

        resetForm(form_name) {
            let form_element = _.get(this.$refs, form_name, null)

            if (!form_element) {
                return
            }

            form_element.resetFields()
            setTimeout(() => {
                form_element.clearValidate()
            }, 100)
        },

        transformUrl(value) {
            // Preserve casing in the host and path; only lowercase to detect
            // partial-scheme typing like "Http", "HTTPS:/" etc.
            const trimmed = (value || '').trim();
            const lowered = trimmed.toLowerCase();

            if (['http', 'http:', 'http:/', 'https', 'https:', 'https:/'].includes(lowered) || !trimmed || lowered === 'https://') {
                return '';
            }

            if (lowered.startsWith('http://')) {
                return 'https://' + trimmed.substring(7);
            }

            if (!lowered.startsWith('https://')) {
                return 'https://' + trimmed;
            }

            return trimmed;
        }
    }
}


let html_mixin = {
    methods: {
        sanitizeText(string) {
            if (string) {
                let entityMap = {
                    '&': '&amp;',
                    '<': '&lt;',
                    '>': '&gt;',
                    '"': '&quot;',
                    "'": '&#39;',
                    '/': '&#x2F;',
                    '`': '&#x60;',
                    '=': '&#x3D;'
                }
                return String(string).replace(/[&<>"'`=\/]/g, function (s) {
                    return entityMap[s]
                })
            }
            return ''
        },

        setPageTitle(title) {
            document.title = title
        },

        setPageTitleInfoText(info_text) {
            this.setPageTitle(document.title + ' ' + info_text)
        }
    }
}

const ADDON_LOCAL_PRESENCE_CATEGORY = 1
const US_ALL_AREA_CODES_1000 = 8

let addons_mixin = {
    async mounted() {
        await this.initializeAddons()
    },

    computed: {
        ...mapState(['addons']),

        allAddons() {
            return this.addons
        },

        localPresencePackages() {
            return this.allAddons
                .filter(addon => addon.category == ADDON_LOCAL_PRESENCE_CATEGORY && addon.type !== US_ALL_AREA_CODES_1000)
        },

        countryByType() {
            let dict = {}

            for (let localPresencePackage of this.localPresencePackages) {
                dict[localPresencePackage.type] = localPresencePackage.data.country
            }

            return dict
        },

        typesByCountry() {
            let dict = {}

            for (let localPresencePackage of this.localPresencePackages) {
                if (!dict[localPresencePackage.data.country]) {
                    dict[localPresencePackage.data.country] = []
                }
                dict[localPresencePackage.data.country].push(localPresencePackage.type)
            }

            return dict
        },

        localPresencesByType() {
            let dict = {}

            for (let localPresencePackage of this.localPresencePackages) {
                dict[localPresencePackage.type] = localPresencePackage
            }

            return dict
        },

        localPresencePricesByType() {
            let prices = {}

            for (let localPresencePackage of this.localPresencePackages) {
                let price = 0
                if (localPresencePackage.data?.price) {
                    price = localPresencePackage.data.price
                }
                prices[`${localPresencePackage.type}`] = price
            }

            return prices
        },

        localPresenceOptions() {
            return this.localPresencePackages
                .map(localPresencePackage => {
                    let price = 0
                    if (localPresencePackage.data.price) {
                        price = localPresencePackage.data.price
                    }
                    return {
                        label: `${localPresencePackage.name} | $${price}`,
                        prop: localPresencePackage.type
                    }
                })
        }
    },

    methods: {
        ...mapActions(['setAddons']),

        initializeAddons() {
            let options = {}
            if (this.company) {
                options.params = {company_id: this.company.id}
            }
            return axios.get('/api/admin/addons', options)
                .then(({data}) => {
                    this.setAddons(data.addons)
                })
        }
    }
}

let subscription_style_mixin = {
    data() {
        return {
            statuses: [
                {
                    value: 'active',
                    label: 'Active',
                    color: '#4CAF50'
                },
                {
                    value: 'active_broken',
                    label: 'Active (No Subscription)',
                    color: '#c0ca33'
                },
                {
                    value: 'cancelled',
                    label: 'Cancelled',
                    color: '#b71c1c'
                },
                {
                    value: 'suspended',
                    label: 'Suspended',
                    color: '#607D8B'
                },
                {
                    value: 'deleted',
                    label: 'Deleted',
                    color: '#b71c6a'
                },
                {
                    value: 'in_trial',
                    label: 'In Trial',
                    color: '#00ccff'
                },
            ],
        }
    },
    methods: {
        getSubscriptionStatus(status) {
            let found = this.statuses.find(o => o.value === status)

            if (found) {
                return found.label
            }

            return '-'
        },

        getSubscriptionStyle(status) {
            let found = this.statuses.find(o => o.value === status)

            if (found) {
                return {
                    color: found.color
                }
            }

            return {
                color: '#000000'
            }
        },
    }
}

let acl_mixin = {
    data() {
        return {
            auth: auth
        }
    },

    computed: {
        isSuperAdmin() {
            return this.auth?.user?.profile?.is_super_admin
        },

        isCarrierOpsAdmin() {
            return this.auth?.user?.profile?.carrier_ops_admin
        },

        isRevOpsAdmin() {
            return this.auth?.user?.profile?.is_rev_ops_admin
        },

        isPartnershipAdmin() {
            return this.auth?.user?.profile?.partnership_admin
        },

        isSalesAdmin() {
            return this.auth?.user?.profile?.sales_admin
        },

        isSalesRep() {
            return this.auth?.user?.profile?.is_sales_rep
        },

        isReseller() {
            return this.auth?.user?.profile?.is_reseller
        },

        isModgenius() {
            return this.auth?.user?.profile?.reseller_id === 2132
        },

        // allow only certain admin types to see action buttons
        canSeeHeaderButtons() {
            return this.auth?.user?.profile?.regular_admin
        },

        canSeeAddAccountButton() {
            return this.canSeeHeaderButtons && this.canAddAccount
        },

        isSuperAdminOrReseller() {
            return this.isReseller || this.isSuperAdmin
        },

        allowedToManageCompanyResellers() {
            // only Alo Super Admins and partnership admins can manage company resellers
            // Reseller admins can't manage company resellers
            if (this.isReseller) {
                return false
            }

            return this.isSuperAdmin || this.isPartnershipAdmin
        },

        isInternationalAdmin() {
            return this.auth?.user?.profile?.is_international_admin
        },
    }
}

let kyc_mixin = {
    methods: {
        calculateTrialDaysRemaining(company) {
            if (!company.subscription) {
                return 0
            }

            let trialStartDate = company.subscription.trial_start
            let trialEndDate = company.subscription.trial_end

            if (!trialStartDate || !trialEndDate) {
                return 0
            }

            const trialStart = new Date(trialStartDate)
            const trialEnd = new Date(trialEndDate)
            const today = new Date()

            if (trialStart > today) {
                return 0
            }

            if (trialEnd < today) {
                return 0
            }

            const diffTime = Math.abs(trialEnd - today)
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

            return diffDays
        }
    }
}

let compliance_mixin = {
    methods: {
        getCountries() {
            const countries = window.countriesAndTimezones.getAllCountries()

            // Exclude US & CA, and then extract only id, name fields of the countries
            const countries_array = Object.values(countries)
                .filter(({id}) => !['US', 'CA'].includes(id))
                .map(({id, name}) => {
                    return {id, name}
                })

            // Add US & CA to the top and return
            return [
                {id: 'US', name: 'United States'},
                {id: 'CA', name: 'Canada'},
                ...countries_array,
            ]
        },

        fixToUcFirst(text = null) {
            if (!text) {
                return ''
            }
            return text
                .toLowerCase()
                .split('_')
                .map(word => this.$options.filters.ucfirst(word))
                .join(' ');
        },
    }
}

let prompt_mixin = {
    methods: {
        promptImpersonation(is_reseller = false) {
            return new Promise((resolve, reject) => {
                const promptMsg = is_reseller ? 'Why you are impersonating this account?' : 'Why you are impersonating this user?';
                const promptHeader = is_reseller ? 'Impersonate Account' : 'Impersonate User';
                const promptErrorMsg = is_reseller ? 'You can not impersonate an account without a reason.' : 'You can not impersonate a user without a reason.';

                this.$prompt(promptMsg, promptHeader, {
                    confirmButtonText: 'Impersonate',
                    cancelButtonText: 'Cancel'
                }).then(({ value }) => {
                    value = (value || '').trim();

                    if (!value) {
                        this.$notify.error({
                            title: 'Error',
                            message: promptErrorMsg
                        });
                        return;
                    }

                    resolve(value);
                }).catch(err => {
                    console.log(err);
                });
            });
        }
    }
}

let format_mixin = {
    methods: {
        cleanupDeletedTag(tag) {
            if (!tag.includes('_deleted_')) {
                return tag
            }

            // Revove everything after the first _
            return tag.split('_')[0] + ' (deleted)'
        },
    }
}

let search_mixin = {
    methods: {
        searchPhoneNumbers(incoming_numbers, search_criteria) {
            const phone_numbers = incoming_numbers.filter(incoming_number => {
                let searchable_number = incoming_number.phone_number

                // Filter phone numbers before any "_"
                if (incoming_number.phone_number.includes('_')) {
                    searchable_number = searchable_number.split('_')[0]
                }

                // Filter phone numbers after any "+"
                if (incoming_number.phone_number.includes('+')) {
                    searchable_number = searchable_number.split('+')[1]
                }

                return searchable_number.includes(search_criteria)
            })

            return phone_numbers
        }
    }
}

let reseller_mixin = {
    computed: {
        canAddAccount() {
            const reseller = this.auth?.user?.profile?.reseller
            if (!reseller || !reseller.id) {
                return true
            }
            
            const resellerId = parseInt(reseller.id)
            if (isNaN(resellerId) || resellerId <= 0) {
                return true
            }
            
            // Access store directly
            const storeSettings = this.$store.state.resellerSettings[resellerId]
            
            // If settings exist and can_create_accounts is false, restrict access
            if (storeSettings && storeSettings.can_create_accounts === false) {
                return false
            }
            
            // Call loadResellerSettings to ensure settings are loaded if not present
            if (!storeSettings) {
                this.loadResellerSettings()
            }
            
            // Default to allowing account creation
            return true
        }
    },
    
    methods: {
        loadResellerSettings() {
            const reseller = this.auth?.user?.profile?.reseller
            if (!reseller || !reseller.id) {
                return null
            }
            
            // Ensure we have a valid numeric ID
            const resellerId = parseInt(reseller.id)
            if (isNaN(resellerId) || resellerId <= 0) {
                return null
            }
            
            // Get from Vuex store
            const storeSettings = this.$store.state.resellerSettings[resellerId]
            
            // If not in store, fetch from API
            if (!storeSettings) {
                this.fetchResellerSettingsFromApi(resellerId)
                return null
            }
            
            return this.$store.state.resellerSettings
        },
        
        fetchResellerSettingsFromApi(resellerId) {
            axios.get(`/api/admin/reseller/settings`)
                .then(response => {
                    // Directly commit the mutation
                    this.$store.commit('SET_RESELLER_SETTINGS', {
                        id: resellerId,
                        settings: response.data
                    })
                })
                .catch(error => {
                    console.error('Error loading reseller settings:', error)

                    // Set default values on error
                    this.$store.commit('SET_RESELLER_SETTINGS', {
                        id: resellerId, 
                        settings: { 
                            can_create_accounts: true
                        }
                    })
                })
        }
    }
}

export {
    paginator_mixin,
    validator_mixin,
    form_validation_mixin,
    html_mixin,
    addons_mixin,
    subscription_style_mixin,
    acl_mixin,
    kyc_mixin,
    compliance_mixin,
    prompt_mixin,
    format_mixin,
    search_mixin,
    reseller_mixin,
}
